import { useBlocker } from "@tanstack/react-router";
import { createColumnHelper } from "@tanstack/react-table";
import { useVirtualizer } from "@tanstack/react-virtual";
import {
  ActivityQuery,
  AuthConfig,
  createRecordFromArray,
  ListTestResponse,
  queries,
  TestsActivity,
  Threat,
  useAuthConfig,
} from "@theprelude/core";
import {
  Button,
  CloseIcon,
  Dropdown,
  DropdownContent,
  DropdownItem,
  DropdownTrigger,
  IconButton,
  Input,
  InputField,
  notifyError,
  notifySuccess,
  SearchIcon,
  TrashcanIcon,
  Truncate,
} from "@theprelude/ds";
import { useMemo, useRef, useState } from "react";
import { Controller, useForm } from "react-hook-form";
import { v4 } from "uuid";
import { Highlight } from "@/components/highlight";
import { createQuery } from "@/lib/create-query";
import { fuzzyFilter } from "@/lib/fuzzy-filters";
import { getPersonalPercentages, OptionalODPFields } from "@/lib/odp";
import {
  authorColumn,
  defaultHidden,
  getColumnVisibility,
  odpColumns,
  orderColumn,
  techniqueColumn,
  testIdColumn,
  testNameSmallColumn,
  testProtectedColumn,
} from "../table/columns";
import { DndTable, RowDragHandleCell } from "../table/dnd-table";
import { useTable } from "../table/hooks";
import {
  UpdateThreatForm,
  useUpdateOwnerThreatContents,
} from "./use-update-threat";

interface EditThreatProps {
  threat: Threat;
  setMode: (mode: "edit" | "view") => void;
  query: ActivityQuery;
}

export type ThreatActivityEntry = OptionalODPFields & {
  rowId: string;
  name: string;
  testId: string;
  account_id: string | undefined;
  author: string | undefined;
  technique?: string;
  protected?: number;
  benchmark?: number;
};

function getThreatOverlayData(
  threat: Threat,
  tests: ListTestResponse[],
  testsActivity: TestsActivity[],
) {
  const testActivity = testsActivity.filter((test) =>
    threat.tests.includes(test.id),
  );

  const testActivityById = createRecordFromArray(testActivity, "id");

  const rows: ThreatActivityEntry[] = tests
    .filter((t) => t.tombstoned === null)
    .map((t) => {
      const usage = testActivityById[t.id];
      return {
        rowId: t.id,
        name: t.name,
        testId: t.id,
        account_id: t.account_id ?? undefined,
        author: t.author ?? undefined,
        technique: t.technique ?? undefined,
        protected: usage?.protected ?? undefined,
        ...getPersonalPercentages(usage?.personal),
      };
    });

  return {
    result: {
      rows,
    },
    error: null,
  };
}

const columnHelper = createColumnHelper<ThreatActivityEntry>();

const columns = [
  columnHelper.accessor("testId", {
    ...orderColumn,
    cell: ({ row }) => <RowDragHandleCell rowId={row.id} />,
  }),
  columnHelper.accessor("testId", defaultHidden(testIdColumn)),
  columnHelper.accessor("name", testNameSmallColumn),
  columnHelper.accessor("author", authorColumn),
  columnHelper.accessor("technique", techniqueColumn),
  columnHelper.accessor("protected", testProtectedColumn),
  ...odpColumns(columnHelper),
];

const threatEditQueryOptions = (
  authConfig: AuthConfig,
  query: ActivityQuery,
  id: string,
) =>
  createQuery({
    queries: [
      queries.detect.getThreat(authConfig, id),
      queries.detect.tests(authConfig),
      queries.detect.activity.tests(authConfig, query),
    ],
    queryFn: getThreatOverlayData,
  });

export default function EditThreat({
  threat,
  setMode,
  query,
}: EditThreatProps) {
  const { register, handleSubmit, control, formState } =
    useForm<UpdateThreatForm>({
      defaultValues: {
        name: threat.name,
        source_id: threat.source_id ?? "",
        tests: threat.tests,
      },
    });

  useBlocker(
    () =>
      window.confirm("You have unsaved edits, are you sure you want to leave?"),
    formState.isDirty,
  );

  const updateThreatMutation = useUpdateOwnerThreatContents();

  const onSubmit = async (data: UpdateThreatForm) => {
    try {
      await updateThreatMutation.mutateAsync({ threat, form: data });
      notifySuccess({ message: "Threat updated successfully" });
      setMode("view");
    } catch {
      notifyError({ message: "Failed to update threat" });
    }
  };

  return (
    <form onSubmit={handleSubmit(onSubmit)} className="flex flex-col gap-4 p-4">
      <InputField label="Source ID">
        <Input {...register("source_id")} className="h-9 w-64" />
      </InputField>

      <InputField label="Threat Name" required>
        <Input
          {...register("name", { required: "Threat name is required" })}
          className="h-12 text-3xl"
        />
      </InputField>

      <Controller
        control={control}
        name="tests"
        render={({ field }) => (
          <>
            <div className="flex flex-row justify-end">
              <AddTestDropdown
                threat={threat}
                query={query}
                onSelect={(testId) => {
                  field.onChange([...field.value, testId]);
                }}
              />
            </div>

            <section className="pt-2">
              <EditTestsTable
                threat={threat}
                testIds={field.value}
                onChange={(testIds) => {
                  field.onChange(testIds);
                }}
                query={query}
              />
            </section>
          </>
        )}
      />

      <div className="flex gap-2">
        <Button
          type="submit"
          intent="primary"
          disabled={updateThreatMutation.isPending}
          loading={updateThreatMutation.isPending}
        >
          Save Changes
        </Button>
        <Button
          type="button"
          intent="secondary"
          onClick={() => setMode("view")}
          disabled={updateThreatMutation.isPending}
        >
          Cancel
        </Button>
      </div>
    </form>
  );
}

function EditTestsTable({
  threat,
  testIds,
  onChange,
  query,
}: {
  threat: Threat;
  testIds: string[];
  onChange: (testIds: string[]) => void;
  query: ActivityQuery;
}) {
  const authConfig = useAuthConfig();

  const { data: threatData } = threatEditQueryOptions(
    authConfig,
    query,
    threat.id,
  ).useSuspenseQuery();

  const selectedTests = useMemo(() => {
    const allTests = createRecordFromArray(threatData.result.rows, "testId");
    return testIds
      .map((testId) => {
        const t = allTests[testId];

        if (!t) {
          return;
        }

        return { ...t, rowId: v4() };
      })
      .filter((test): test is ThreatActivityEntry => test !== undefined);
  }, [threatData.result.rows, testIds]);

  const cols = useMemo(() => {
    return [
      ...columns,
      columnHelper.display({
        id: "actions",
        enableSorting: false,
        enableResizing: false,
        cell: ({ row }) => {
          return (
            <div className="inline-flex w-full items-center justify-end gap-2">
              <IconButton
                icon={<TrashcanIcon className="fill-secondary-foreground" />}
                onClick={() => {
                  onChange(
                    selectedTests
                      .filter((t) => t.rowId !== row.id)
                      .map((t) => t.testId),
                  );
                }}
              />
            </div>
          );
        },
        size: 50,
      }),
    ];
  }, [onChange, selectedTests]);

  const table = useTable({
    name: "edit-threat-tests",
    data: selectedTests,
    columns: cols,
    initialState: { columnVisibility: getColumnVisibility(cols) },
    getRowId: (row) => row.rowId,
  });

  return (
    <DndTable
      table={table}
      onDragEnd={(rowIds) => {
        const rowsById = createRecordFromArray(selectedTests, "rowId");

        const testIds = rowIds
          .map((r) => rowsById[r]?.testId)
          .filter((test): test is string => test !== undefined);

        onChange(testIds);
      }}
    />
  );
}

function AddTestDropdown({
  onSelect,
  query,
  threat,
}: {
  threat: Threat;
  onSelect: (testId: string) => void;
  query: ActivityQuery;
}) {
  const authConfig = useAuthConfig();
  const { data: threatData } = threatEditQueryOptions(
    authConfig,
    query,
    threat.id,
  ).useSuspenseQuery();

  const tests = threatData.result.rows;

  const [testSearch, setTestSearch] = useState("");

  const filteredTests = useMemo(() => {
    return tests.filter((t) => fuzzyFilter(`${t.name}`, testSearch));
  }, [testSearch, tests]);

  return (
    <Dropdown>
      <DropdownTrigger asChild>
        <Button type="button" intent="primary">
          Add Test
        </Button>
      </DropdownTrigger>
      <DropdownContent align="start" className="w-96">
        <Input
          className="rounded-none border-stroke-line border-x-0 border-t-0 border-b"
          value={testSearch}
          onChange={(ev) => setTestSearch(ev.target.value)}
          before={<SearchIcon />}
          after={
            <IconButton
              icon={<CloseIcon />}
              onClick={() => {
                setTestSearch("");
              }}
            />
          }
          placeholder="Search by test"
        />

        <TestList
          tests={filteredTests}
          onSelect={onSelect}
          testSearch={testSearch}
          setTestSearch={setTestSearch}
        />
      </DropdownContent>
    </Dropdown>
  );
}

function TestList({
  tests,
  onSelect,
  testSearch,
  setTestSearch,
}: {
  tests: ThreatActivityEntry[];
  onSelect: (testId: string) => void;
  testSearch: string;
  setTestSearch: (testSearch: string) => void;
}) {
  const parentRef = useRef<HTMLDivElement>(null);

  const rowVirtualizer = useVirtualizer({
    count: tests.length,
    getScrollElement: () => parentRef.current,
    estimateSize: () => 36,
    measureElement:
      typeof window !== "undefined" &&
      navigator.userAgent?.indexOf("Firefox") === -1
        ? (element) => element?.getBoundingClientRect().height
        : undefined,
    overscan: 5,
  });

  return (
    <div ref={parentRef} className="h-[300px] overflow-y-auto">
      <div
        style={{
          height: `${rowVirtualizer.getTotalSize()}px`,
          width: "100%",
          position: "relative",
        }}
      >
        {rowVirtualizer.getVirtualItems().map((virtualItem) => {
          const test = tests[virtualItem.index] as ThreatActivityEntry;

          return (
            <DropdownItem
              key={test.testId}
              onSelect={() => {
                onSelect(test.testId);
                setTestSearch("");
              }}
              style={{
                position: "absolute",
                top: 0,
                left: 0,
                width: "100%",
                height: `${virtualItem.size}px`,
                transform: `translateY(${virtualItem.start}px)`,
              }}
            >
              <div className="flex items-center justify-between gap-1">
                <Truncate text={`${test.name}`}>
                  <Highlight text={`${test.name}`} highlight={testSearch} />
                </Truncate>
              </div>
            </DropdownItem>
          );
        })}
      </div>
    </div>
  );
}
